import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  canInsertStationRouteWaypoint,
  createErrorResult,
  editFormDataAF,
  EditFormMode,
  errors,
  generateStationRouteWaypointId,
  insertStationRouteWaypoint,
  IStationRouteComputedData,
  IStationRouteDto,
  IStationRouteStationInfo,
  IStationRouteWaypointLocationDraft,
  normalizeStationRouteComputedData,
  resolveStationRouteWaypointAreaId,
  resolveStationRouteWaypointInsertAfterIndex,
  resolveStationRouteWaypointName,
  SimpleResult,
  toWaypointMapStations,
  useTimezone,
  WaypointCategoryEnum,
  ZERO_TIME_HHmm,
} from "@ryde/shared";

import { useCrudEditContext } from "../../contexts";
import { useStationRouteDtoActions } from "./use-station-route-dto-actions";

export const useStationRouteWaypointActions = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { mode } = useCrudEditContext<IStationRouteDto>();
  const [stationRouteDto] = useRecoilState<
    Partial<IStationRouteDto> | undefined
  >(editFormDataAF(["stationRoute", mode || EditFormMode.CrudRoute]));

  const {
    updateEditingStationRouteComputedData,
    stationRouteRebuildRouteHandler,
  } = useStationRouteDtoActions();

  const stations = useMemo(
    () =>
      toWaypointMapStations(stationRouteDto?.computedData?.stationInfos ?? []),
    [stationRouteDto?.computedData?.stationInfos],
  );

  const addWaypointFromDraft = useEvent(
    async (
      draft: IStationRouteWaypointLocationDraft,
    ): Promise<SimpleResult<void>> => {
      if (!stations.length || !canInsertStationRouteWaypoint(stations)) {
        return createErrorResult(
          errors.common.technicalProblemOccurred,
          "Cannot add waypoint",
        );
      }

      const insertAfterIndex =
        draft.insertAfterIndex ??
        resolveStationRouteWaypointInsertAfterIndex({
          stations,
          latitude: draft.latitude,
          longitude: draft.longitude,
          routePolyline: stationRouteDto?.result?.routePolyline,
        });

      const areaId = await resolveStationRouteWaypointAreaId({
        latitude: draft.latitude,
        longitude: draft.longitude,
        stations,
        insertAfterIndex,
      });
      const waypoint: IStationRouteStationInfo = {
        id: generateStationRouteWaypointId(),
        name: resolveStationRouteWaypointName({
          customName: draft.customName,
          fullAddress: draft.fullAddress,
          fallbackName: t("stationRoute.waypoint.DEFAULT_NAME"),
        }),
        locationInfo: {
          latitude: draft.latitude,
          longitude: draft.longitude,
        },
        timeArrival: ZERO_TIME_HHmm,
        waypointCategory: WaypointCategoryEnum.MLPathWaypoint,
        areaId,
      };

      const sourceStations = stationRouteDto?.computedData?.stationInfos ?? [];
      const stationInfosUpdated = insertStationRouteWaypoint({
        stations: sourceStations,
        waypoint,
        insertAfterIndex,
      });

      const computedData: IStationRouteComputedData = {
        ...(stationRouteDto?.computedData || {}),
        stationInfos: stationInfosUpdated,
      };

      const normalizedComputedData = normalizeStationRouteComputedData(
        computedData,
        false,
        timezone,
      );

      const result = await updateEditingStationRouteComputedData({
        computedData: normalizedComputedData,
        direction: stationRouteDto?.metadata?.direction,
        optimize: false,
      });

      if (result.success) {
        return result;
      }

      const localResult = await updateEditingStationRouteComputedData({
        computedData: normalizedComputedData,
        direction: stationRouteDto?.metadata?.direction,
        optimize: false,
        skipApiCheck: true,
      });

      if (!localResult.success) {
        return localResult;
      }

      return stationRouteRebuildRouteHandler(false);
    },
  );

  return {
    addWaypointFromDraft,
    stationRouteDto,
    stations,
  };
};
